import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Tag,
  UploadFile
} from 'antd'
import { UploadChangeParam } from 'antd/es/upload'
import { FormInstance } from 'antd/lib'
import { useParams } from 'react-router-dom'

import {
  ClassifierCategory,
  ClassifierType,
  InventoryDocumentState,
  Permissions
} from 'src/api/requests'
import {
  ClassifierInfiniteSelect,
  CustomItemRender,
  requiredRules,
  Text,
  Upload
} from 'src/components'
import { SettlementsConsumablesTable } from 'src/features/stock/components/settlements'
import { useClassifierId, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { getEntryStatus } from '../../utils'

import { useStyles } from './styles'

type Props = {
  form: FormInstance
  state?: InventoryDocumentState
  onChange?: (info: UploadChangeParam<UploadFile>) => void
}

export const CreateForm = ({ form, state, onChange }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { id } = useParams()

  const { hasPermission } = useUser()
  const isAdmin = hasPermission(Permissions.STOCKS_MENU_ACCESS_ISSUANCE)

  const reasonsId = useClassifierId(
    isAdmin
      ? ClassifierType.RETURN_REASON
      : ClassifierType.INTERNAL_RETURN_REASON,
    ClassifierCategory.STOCKS
  )

  const medicineItems = Form.useWatch('medicine', form)

  const total = medicineItems?.reduce(
    (acc: number, item: { priceTotal: number }) =>
      acc + (item?.priceTotal ?? 0),
    0
  )

  return (
    <Flex vertical gap={24}>
      <Card>
        <Flex vertical gap={24}>
          <Flex justify="space-between" align="center">
            <Text weight={700} size="xxl">
              {t('stock_write_offs.write_off')}
            </Text>

            {!!state && (
              <Tag color={getEntryStatus(t, state)?.color}>
                {getEntryStatus(t, state)?.text}
              </Tag>
            )}
          </Flex>
          <Card className={styles.detailsCard}>
            <Flex vertical gap={24}>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="documentDate"
                    label={t(`analysis.date`)}
                    rules={requiredRules}
                  >
                    <DatePicker
                      className="full-width"
                      format={dayMonthYearBackSlash}
                      disabled={!!id}
                    />
                  </Form.Item>
                </Col>

                {id && (
                  <Col span={12}>
                    <Form.Item
                      name="documentNumber"
                      label={t(`stock_write_offs.registration_number`)}
                    >
                      <Input
                        className="full-width"
                        placeholder={t(`stock_write_offs.registration_number`)}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                )}

                <Col span={12}>
                  <Form.Item
                    name="reasons"
                    label={t(`stock.return_reason`)}
                    rules={requiredRules}
                  >
                    <ClassifierInfiniteSelect
                      labelInValue
                      id={reasonsId}
                      mode="multiple"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Flex>
          </Card>
        </Flex>
      </Card>

      <Card>
        <Form.List name="medicine">
          {(fields, { add, remove }) => (
            <SettlementsConsumablesTable
              fields={fields}
              add={add}
              remove={remove}
              form={form}
            />
          )}
        </Form.List>

        <Flex justify="end" align="center" gap={8}>
          <Text weight={700}>{t('common.total')}: </Text>
          <Text weight={700}>{total ?? 0} MDL</Text>
        </Flex>
      </Card>

      <Card>
        <Card title={t('stock_applications.uploaded_documents')}>
          <Form.Item name="docs" className="full-width">
            <Upload
              onChange={onChange}
              customRequest={() => null}
              itemRender={(_, file, __, { remove }) => (
                <CustomItemRender file={file} onRemove={remove} />
              )}
            />
          </Form.Item>
        </Card>
      </Card>

      <Card>
        <Card title={t('common.comment')}>
          <Form.Item name="comment" className="full-width">
            <Input.TextArea rows={5} placeholder={t('common.leave_comment')} />
          </Form.Item>
        </Card>
      </Card>
    </Flex>
  )
}
